/**
 * Gentle Belle Theme - Default Configuration
 *
 * SINGLE SOURCE OF TRUTH for theme colors
 *
 * This file contains the default theme configuration for the Gentle Belle design system.
 * All components and theme editors should use these defaults for consistency.
 *
 * IMPORTANT: When updating colors, keep these files in sync:
 * 1. THIS FILE (frontend/src/config/defaultTheme.ts) - PRIMARY SOURCE
 * 2. workers/src/types.ts - Backend default theme
 * 3. frontend/src/styles/tokens.css - CSS variables (manual sync)
 *
 * Color Palette:
 * - Primary: Beige tones (#C4A484, #E8D5C4, #A88B6E)
 * - Background: Light cream/beige (#FAF8F5, #F5F1EA, #F0EAE0)
 * - Text: Warm brown tones (#3E3328, #6D5F52, #9C8E81)
 * - Border: Soft beige (#E8E2D8, #DDD5C9)
 */

import type { ThemeConfig } from '../types/theme';

/**
 * Default colors for Gentle Belle theme
 */
export const DEFAULT_COLORS = {
  primary: '#C4A484',
  primaryHover: '#E8D5C4',
  primaryDark: '#A88B6E',
  bgPrimary: '#FAF8F5',
  bgSecondary: '#F5F1EA',
  bgTertiary: '#F0EAE0',
  bgLight: '#FAFAFA',
  textPrimary: '#3E3328',
  textSecondary: '#6D5F52',
  textTertiary: '#9C8E81',
  textOnDark: '#FAF8F5',
  textOnDarkSecondary: 'rgba(250, 248, 245, 0.7)',
  borderPrimary: '#E8E2D8',
  borderSecondary: '#DDD5C9',
  gradientPrimary: 'linear-gradient(135deg, #E8D5C4, #C4A484)',
  gradientHover: 'linear-gradient(135deg, #C4A484, #A88B6E)',
} as const;

/**
 * Default shadows for Gentle Belle theme
 */
export const DEFAULT_SHADOWS = {
  desktop: {
    primary: '0 4px 16px rgba(168, 139, 110, 0.12)',
    primaryHover: '0 8px 32px rgba(168, 139, 110, 0.18)',
    card: '0 2px 8px rgba(168, 139, 110, 0.08)',
    cardHover: '0 4px 16px rgba(168, 139, 110, 0.12)',
    header: '0 2px 8px rgba(168, 139, 110, 0.08)',
  },
  mobile: {
    primary: '0 2px 8px rgba(168, 139, 110, 0.08)',
    primaryHover: '0 4px 16px rgba(168, 139, 110, 0.12)',
    card: '0 1px 4px rgba(168, 139, 110, 0.06)',
    cardHover: '0 2px 8px rgba(168, 139, 110, 0.1)',
    header: '0 1px 2px rgba(168, 139, 110, 0.05)',
  },
} as const;

/**
 * Default typography settings
 */
export const DEFAULT_TYPOGRAPHY = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontFamilyMono: "monospace",
  fontSizeBase: '16px',
  fontSizeScale: 1.0,
  lineHeight: 1.5,
  letterSpacing: 'normal',
} as const;

/**
 * Default spacing settings
 */
export const DEFAULT_SPACING = {
  desktop: {
    baseUnit: '1rem',
    scale: 1.0,
    borderRadiusSmall: '8px',
    borderRadiusMedium: '12px',
    borderRadiusLarge: '16px',
  },
  mobile: {
    baseUnit: '4px',
    scale: 1.0,
    borderRadiusSmall: '8px',
    borderRadiusMedium: '12px',
    borderRadiusLarge: '16px',
  },
} as const;

/**
 * Default brand settings
 */
export const DEFAULT_BRAND = {
  siteName: '溫',
  logoUrl: '',
  faviconUrl: '',
  ogImageUrl: '',
} as const;

/**
 * Default layout settings
 */
export const DEFAULT_LAYOUT = {
  desktop: {
    headerHeight: '44px',
    maxContentWidth: '1400px',
  },
  mobile: {
    headerHeight: '56px',
    maxContentWidth: '100%',
  },
} as const;

/**
 * Complete default theme configuration for desktop
 */
export const DEFAULT_DESKTOP_THEME: ThemeConfig = {
  colors: { ...DEFAULT_COLORS },
  typography: { ...DEFAULT_TYPOGRAPHY },
  spacing: { ...DEFAULT_SPACING.desktop },
  shadows: { ...DEFAULT_SHADOWS.desktop },
  brand: { ...DEFAULT_BRAND },
  layout: { ...DEFAULT_LAYOUT.desktop },
  metadata: {
    version: 'v1',
    deviceType: 'desktop',
    updatedAt: new Date().toISOString(),
  },
};

/**
 * Complete default theme configuration for mobile
 */
export const DEFAULT_MOBILE_THEME: ThemeConfig = {
  colors: { ...DEFAULT_COLORS },
  typography: {
    ...DEFAULT_TYPOGRAPHY,
    letterSpacing: '-0.01em',
  },
  spacing: { ...DEFAULT_SPACING.mobile },
  shadows: { ...DEFAULT_SHADOWS.mobile },
  brand: { ...DEFAULT_BRAND },
  layout: { ...DEFAULT_LAYOUT.mobile },
  metadata: {
    version: 'v1',
    deviceType: 'mobile',
    updatedAt: new Date().toISOString(),
  },
};

/**
 * Get default theme for device type
 */
export function getDefaultTheme(device: 'desktop' | 'mobile'): ThemeConfig {
  return device === 'desktop' ? DEFAULT_DESKTOP_THEME : DEFAULT_MOBILE_THEME;
}

/**
 * CSS variables string for preview/modals
 */
export function getDefaultCSSVariables(): string {
  return `
    --color-primary: ${DEFAULT_COLORS.primary};
    --color-primaryHover: ${DEFAULT_COLORS.primaryHover};
    --color-primaryDark: ${DEFAULT_COLORS.primaryDark};
    --color-bgPrimary: ${DEFAULT_COLORS.bgPrimary};
    --color-bgSecondary: ${DEFAULT_COLORS.bgSecondary};
    --color-textPrimary: ${DEFAULT_COLORS.textPrimary};
    --color-textSecondary: ${DEFAULT_COLORS.textSecondary};
    --color-borderPrimary: ${DEFAULT_COLORS.borderPrimary};
    --color-gradientPrimary: ${DEFAULT_COLORS.gradientPrimary};
    --radius-sm: ${DEFAULT_SPACING.desktop.borderRadiusSmall};
    --radius-md: ${DEFAULT_SPACING.desktop.borderRadiusMedium};
  `;
}
